// Le compte à terme : un versement bloqué pour une durée choisie, à taux garanti.
// Les intérêts, versés à l'échéance, sont imposés au prélèvement forfaitaire unique (31,4 % en 2026).
// Sortir avant l'échéance fait perdre la moitié des intérêts acquis, et tous avant un mois.
// L'argent bloqué ne compte pas dans le fonds d'urgence : il n'est pas disponible.
import { round2 } from '../engine/finance';
import { fmtEur, fmtPct } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, TermDeposit } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';
import { addJournal, addNotice } from './actions';

/** Les durées proposées et leur taux brut, des ordres de grandeur de 2026. */
export const TERM_OFFERS: { years: number; rate: number }[] = [
  { years: 1, rate: 0.025 },
  { years: 2, rate: 0.027 },
  { years: 3, rate: 0.03 },
];

export const TERM_DEPOSIT = {
  /** Versement minimum à l'ouverture. */
  minimum: 1000,
  /** Prélèvement forfaitaire unique sur les intérêts : 12,8 % d'impôt et 18,6 % de prélèvements sociaux. */
  tax: 0.314,
  /** Sortie anticipée : la moitié des intérêts acquis est perdue… */
  earlyPenalty: 0.5,
  /** …et tous avant un mois. */
  minDaysForInterest: 30,
};

/** « 1 an », « 2 ans ». */
export function yearsLabel(years: number): string {
  return years === 1 ? t('common.oneYear') : t('common.years', { count: years });
}

/** Intérêts bruts d'un capital placé pendant un nombre de jours, à taux annuel composé. */
export function grossInterest(amount: number, rate: number, days: number): number {
  return amount * (Math.pow(1 + rate, Math.max(0, days) / DAYS_PER_YEAR) - 1);
}

/** Ce qui reste des intérêts après l'impôt. */
export function netInterest(gross: number): number {
  return gross * (1 - TERM_DEPOSIT.tax);
}

/** Intérêts bruts acquis à ce jour. */
export function accruedTermInterest(state: GameState, deposit: TermDeposit): number {
  return grossInterest(deposit.amount, deposit.rate, Math.min(state.day, deposit.endDay) - deposit.startDay);
}

/** Les intérêts bruts qu'on toucherait en sortant aujourd'hui : moitié perdue, et rien avant un mois. */
export function earlyExitGross(state: GameState, deposit: TermDeposit): number {
  if (state.day - deposit.startDay < TERM_DEPOSIT.minDaysForInterest) return 0;
  return accruedTermInterest(state, deposit) * (1 - TERM_DEPOSIT.earlyPenalty);
}

export function openTermDeposit(state: GameState, amount: number, years: number): void {
  const offer = TERM_OFFERS.find((item) => item.years === years);
  const value = Math.floor(amount / 100) * 100;
  if (!offer || value < TERM_DEPOSIT.minimum || state.cash < value) return;
  state.cash = round2(state.cash - value);
  const id = Math.max(0, ...state.termDeposits.map((deposit) => deposit.id)) + 1;
  state.termDeposits = [
    ...state.termDeposits,
    { id, amount: value, rate: offer.rate, years, startDay: state.day, endDay: state.day + years * DAYS_PER_YEAR },
  ];
  addJournal(state, 'journal.termOpen', {
    amount: fmtEur(value),
    duration: yearsLabel(years),
    rate: fmtPct(offer.rate),
  });
}

/** Rend le capital et les intérêts nets sur le compte courant ; renvoie l'impôt et le net versés. */
function payOut(state: GameState, deposit: TermDeposit, gross: number): { tax: number; net: number } {
  const roundedGross = round2(gross);
  const tax = round2(roundedGross * TERM_DEPOSIT.tax);
  const net = round2(roundedGross - tax);
  state.termDeposits = state.termDeposits.filter((item) => item.id !== deposit.id);
  state.cash = round2(state.cash + deposit.amount + net);
  state.totalInterest += net;
  return { tax, net };
}

/** Chaque jour : un compte arrivé à échéance rend son capital et ses intérêts nets sur le compte courant. */
export function matureTermDeposits(state: GameState): void {
  for (const deposit of state.termDeposits.filter((item) => state.day >= item.endDay)) {
    const gross = grossInterest(deposit.amount, deposit.rate, deposit.endDay - deposit.startDay);
    const { tax, net } = payOut(state, deposit, gross);
    const params = {
      amount: fmtEur(deposit.amount),
      gross: fmtEur(gross),
      tax: fmtEur(tax),
      total: fmtEur(deposit.amount + net),
    };
    addNotice(state, 'term.matured', params);
    addJournal(state, 'term.matured', params);
  }
}

/** Sortir avant l'échéance : le capital revient, avec des intérêts réduits. */
export function closeTermDepositEarly(state: GameState, id: number): void {
  const deposit = state.termDeposits.find((item) => item.id === id);
  if (!deposit) return;
  const { net } = payOut(state, deposit, earlyExitGross(state, deposit));
  addJournal(state, 'term.closedEarly', { amount: fmtEur(deposit.amount), net: fmtEur(net) });
}

/** Ce que valent aujourd'hui les comptes à terme : le capital et les intérêts nets déjà acquis. */
export function termDepositsValue(state: GameState): number {
  return state.termDeposits.reduce(
    (sum, deposit) => sum + deposit.amount + netInterest(accruedTermInterest(state, deposit)),
    0,
  );
}

/** Ce qu'ils rapportent en moyenne chaque mois, nets d'impôt : ils comptent dans les revenus passifs. */
export function termDepositsMonthlyIncome(state: GameState): number {
  return state.termDeposits.reduce((sum, deposit) => sum + netInterest(deposit.amount * deposit.rate) / 12, 0);
}
